import { LEASE_STORE, openMiningDatabase, requestResult, transactionDone } from "./checkpoint-store";
import type { MiningLeaseFence, MiningTabLease } from "./types";

type LeaseRow = { key: string; ownerId: string; generation: number; expiresAtMs: number };
type WebLock = { name: string };
type WebLocks = { request<T>(name: string, options: { mode: "exclusive"; ifAvailable: true },
  callback: (lock: WebLock | null) => Promise<T>): Promise<T> };

const HEARTBEAT_MS = 2_000;
const LEASE_TTL_MS = 7_000;

/**
 * Cross-tab exclusion. Web Locks is preferred. IndexedDB's serialized
 * readwrite transaction is the fail-safe fallback; localStorage is never used
 * as an authority because its read/modify/write sequence is racy.
 */
export class BrowserMiningTabLease implements MiningTabLease {
  private readonly ownerId = crypto.randomUUID();
  private key: string | null = null;
  private generation = 0;
  private isHeld = false;
  private releaseWebLock: (() => void) | null = null;
  private webLockRun: Promise<unknown> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private ownershipRevision = 0;
  private usesWebLock = false;
  private expiresAtMs = 0;

  async acquire(key: string, signal?: AbortSignal): Promise<boolean> {
    if (this.held()) return this.key === key;
    if (this.key) await this.release();
    if (signal?.aborted) return false;
    const locks = (navigator as Navigator & { locks?: WebLocks }).locks;
    if (locks) return this.acquireWebLock(locks, key, signal);
    return this.acquireIndexedDbLease(key, signal);
  }

  held(): boolean { return this.isHeld && (this.usesWebLock || this.expiresAtMs > Date.now()); }

  fence(key: string): MiningLeaseFence | null {
    if (!this.held() || this.key !== key) return null;
    const revision = this.ownershipRevision;
    return { key, isCurrent: () => this.held() && this.key === key && this.ownershipRevision === revision,
      ...(!this.usesWebLock ? { indexedDb: { ownerId: this.ownerId, generation: this.generation } } : {}) };
  }

  async release(): Promise<void> {
    if (!this.key) return;
    const key = this.key;
    const generation = this.generation;
    this.isHeld = false;
    this.ownershipRevision++;
    this.key = null;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    this.releaseWebLock?.();
    this.releaseWebLock = null;
    if (this.webLockRun) {
      await this.webLockRun.catch(() => undefined);
      this.webLockRun = null;
      return;
    }
    await this.deleteOwnedIndexedDbLease(key, generation).catch(() => undefined);
  }

  async dispose(): Promise<void> { await this.release(); }

  private async acquireWebLock(locks: WebLocks, key: string, signal?: AbortSignal): Promise<boolean> {
    let settleGranted!: (value: boolean) => void;
    let rejectGranted!: (reason: unknown) => void;
    const granted = new Promise<boolean>((resolve, reject) => { settleGranted = resolve; rejectGranted = reject; });
    const hold = new Promise<void>((resolve) => { this.releaseWebLock = resolve; });
    // Web Locks forbids combining ifAvailable with signal. Keep acquisition
    // non-waiting and check cancellation in the asynchronous grant callback.
    this.webLockRun = locks.request(key, { mode: "exclusive", ifAvailable: true },
      async (lock) => {
        if (signal?.aborted) { rejectGranted(signal.reason); return; }
        if (!lock) { settleGranted(false); return; }
        this.key = key;
        this.usesWebLock = true;
        this.ownershipRevision++;
        this.isHeld = true;
        settleGranted(true);
        await hold;
      }).catch(rejectGranted);
    return granted;
  }

  private async acquireIndexedDbLease(key: string, signal?: AbortSignal): Promise<boolean> {
    if (signal?.aborted) return false;
    const database = await openMiningDatabase();
    const now = Date.now();
    try {
      const transaction = database.transaction(LEASE_STORE, "readwrite");
      const store = transaction.objectStore(LEASE_STORE);
      const existing = await requestResult<LeaseRow | undefined>(store.get(key));
      if (existing && existing.expiresAtMs > now && existing.ownerId !== this.ownerId) {
        transaction.abort();
        return false;
      }
      this.generation = (existing?.generation ?? 0) + 1;
      const expiresAtMs = now + LEASE_TTL_MS;
      store.put({ key, ownerId: this.ownerId, generation: this.generation, expiresAtMs });
      await transactionDone(transaction);
      this.key = key;
      this.usesWebLock = false;
      this.expiresAtMs = expiresAtMs;
      this.ownershipRevision++;
      this.isHeld = true;
      this.heartbeat = setInterval(() => {
        this.renewIndexedDbLease().catch(() => {
          // Renewal revokes only its captured ownership revision internally.
          console.error("Mining lease renewal failed.");
        });
      }, HEARTBEAT_MS);
      return true;
    } finally {
      database.close();
    }
  }

  private async renewIndexedDbLease(): Promise<void> {
    if (!this.isHeld || !this.key) return;
    const key = this.key;
    const generation = this.generation;
    const revision = this.ownershipRevision;
    const current = () => this.isHeld && this.key === key && this.ownershipRevision === revision;
    const database = await openMiningDatabase().catch(() => null);
    if (!database) { if (current()) this.revokeIndexedDbLease(); return; }
    try {
      if (!current()) return;
      const transaction = database.transaction(LEASE_STORE, "readwrite");
      const store = transaction.objectStore(LEASE_STORE);
      const existing = await requestResult<LeaseRow | undefined>(store.get(key));
      if (!current()) { transaction.abort(); return; }
      if (!existing || existing.ownerId !== this.ownerId || existing.generation !== generation ||
        existing.expiresAtMs <= Date.now()) {
        transaction.abort();
        this.revokeIndexedDbLease();
        return;
      }
      const expiresAtMs = Date.now() + LEASE_TTL_MS;
      store.put({ ...existing, expiresAtMs });
      await transactionDone(transaction);
      if (current()) this.expiresAtMs = expiresAtMs;
    } catch {
      if (current()) this.revokeIndexedDbLease();
    } finally {
      database.close();
    }
  }

  private revokeIndexedDbLease(): void {
    this.isHeld = false;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  private async deleteOwnedIndexedDbLease(key: string, generation: number): Promise<void> {
    const database = await openMiningDatabase();
    try {
      const transaction = database.transaction(LEASE_STORE, "readwrite");
      const store = transaction.objectStore(LEASE_STORE);
      const existing = await requestResult<LeaseRow | undefined>(store.get(key));
      if (existing?.ownerId === this.ownerId && existing.generation === generation) store.delete(key);
      await transactionDone(transaction);
    } finally {
      database.close();
    }
  }
}

/** Test helper; application code should use BrowserMiningTabLease. */
export class MemoryMiningTabLease implements MiningTabLease {
  private isHeld = false;
  private revision = 0;
  private key: string | null = null;
  constructor(private readonly grant = true) {}
  async acquire(key = ""): Promise<boolean> {
    if (this.isHeld) return this.key === key;
    this.key = key; this.revision++; this.isHeld = this.grant; return this.isHeld;
  }
  held(): boolean { return this.isHeld; }
  fence(key: string): MiningLeaseFence | null {
    const revision = this.revision;
    return this.isHeld && this.key === key ? { key,
      isCurrent: () => this.isHeld && this.key === key && this.revision === revision } : null;
  }
  async release(): Promise<void> { this.isHeld = false; this.revision++; }
  dispose(): void { this.isHeld = false; this.revision++; }
}
